import React, { useState, useEffect } from 'react';
import { Layout, Button, Dropdown, Menu, Avatar, Typography, Drawer, Space, Badge, Tag, Modal, Timeline } from 'antd';
import { 
  MenuOutlined, 
  UserOutlined, 
  LogoutOutlined, 
  SettingOutlined, 
  WalletOutlined,
  CloseOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { useHeartbeat } from '../../hooks/useHeartbeat';
import NetworkStatus from '../NetworkStatus';
import SocketStatus from '../SocketStatus';
import api from '../../services/api';
// import { formatCurrency } from '../../utils/format';

// 临时格式化函数
const formatCurrency = (amount: any): string => {
  if (amount === undefined || amount === null) return '0.00';
  const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
  if (isNaN(numAmount)) return '0.00';
  return numAmount.toFixed(2);
};

const { Header: AntHeader } = Layout;
const { Text } = Typography;

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { siteConfig, loading: configLoading } = useSiteConfig();
  const { isConnected, lastHeartbeat } = useHeartbeat(120000);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [versionInfo, setVersionInfo] = useState<{ version: string; description: string; history: any[] } | null>(null);
  const [versionModalVisible, setVersionModalVisible] = useState(false);

  // 加载版本信息
  useEffect(() => {
    const loadVersionInfo = async () => {
      try {
        const response = await api.get('/version-management/version');
        if (response.data.success) {
          setVersionInfo({
            version: response.data.data.version,
            description: response.data.data.description || '',
            history: response.data.data.history || []
          });
        }
      } catch (error) {
        console.error('加载版本信息失败:', error);
      }
    };
    loadVersionInfo();
  }, []);

  // 调试信息
  console.log('Header组件 - configLoading:', configLoading, 'siteConfig:', siteConfig);

  const handleMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case 'profile':
        navigate('/profile');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'logout':
        logout();
        break;
    }
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const renderLogo = (size: 'small' | 'large' = 'large') => {
    const isSmall = size === 'small';
    const logoSize = isSmall ? '24px' : 'var(--logo-size)';
    const borderRadius = isSmall ? '4px' : '8px';
    const fontSize = isSmall ? '8px' : '14px';

    if (configLoading) {
      return (
        <div 
          className="logo"
          style={{
            width: logoSize,
            height: logoSize,
            borderRadius,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--background-color)',
            opacity: 0.6
          }}
        >
          <span style={{ fontSize }}>...</span>
        </div>
      );
    }

    if (siteConfig?.logo_url) {
      return (
        <img
          src={siteConfig.logo_url}
          alt="Logo"
          style={{
            width: isSmall ? '24px' : `${siteConfig.logo_width || 48}px`,
            height: isSmall ? '24px' : `${siteConfig.logo_height || 48}px`,
            borderRadius,
            objectFit: 'contain'
          }}
          loading="eager"
          decoding="async"
        />
      );
    }

    return (
      <div 
        className="logo"
        style={{
          width: logoSize,
          height: logoSize,
          borderRadius,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <span style={{ fontSize: isSmall ? '10px' : '16px', fontWeight: 'bold' }}>G</span>
      </div>
    );
  };

  const renderSiteName = (isMobile: boolean = false) => {
    const fontSize = isMobile ? '16px' : '20px';
    
    if (configLoading) {
      return (
        <span style={{ 
          fontSize, 
          fontWeight: 'bold', 
          color: 'var(--header-text-color)',
          opacity: 0.6 
        }}>
          加载中...
        </span>
      );
    }

    return (
      <span 
        onClick={() => navigate('/')}
        style={{ 
          fontSize, 
          fontWeight: 'bold', 
          color: 'var(--header-text-color)',
          background: 'var(--header-text-color)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          cursor: 'pointer',
          transition: 'opacity 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.8';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
      >
        {siteConfig?.site_name || ''}
      </span>
    );
  };

  return (
    <>
      <AntHeader
        style={{
          background: 'var(--header-background)',
          borderBottom: '1px solid var(--border-color)',
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 'var(--header-height)',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}
      >
        {/* 左侧：Logo和标题 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
          {/* 移动端菜单按钮 */}
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
            style={{
              display: window.innerWidth < 768 ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2px',
              minWidth: '24px',
              flexShrink: 0
            }}
          />
          
          {/* Logo和标题 - 移动端 */}
          <div style={{ 
            display: window.innerWidth < 768 ? 'flex' : 'none', 
            alignItems: 'center', 
            gap: '2px', 
            flex: 1, 
            minWidth: 0 
          }}>
            {renderLogo('small')}
            <h1 
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: 'var(--header-text-color)',
                background: 'var(--header-text-color)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: 1,
                minWidth: 0
              }}
            >
              {renderSiteName(true)}
            </h1>
            {versionInfo && (
              <Tag 
                color="blue" 
                style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '10px' }}
                onClick={() => setVersionModalVisible(true)}
              >
                v{versionInfo.version}
              </Tag>
            )}
          </div>

          {/* Logo和标题 - 桌面端 */}
          <div style={{ 
            display: window.innerWidth >= 768 ? 'flex' : 'none', 
            alignItems: 'center', 
            gap: '12px' 
          }}>
            {renderLogo('large')}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h1 
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: 'var(--header-text-color)',
                  background: 'var(--header-text-color)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  margin: 0,
                  padding: 0,
                  lineHeight: '1',
                  display: 'inline-block'
                }}
              >
                {renderSiteName(false)}
              </h1>
              {versionInfo && (
                <Tag 
                  color="blue" 
                  style={{ 
                    cursor: 'pointer', 
                    margin: 0,
                    verticalAlign: 'middle',
                    transform: 'translateY(-1px)'
                  }}
                  onClick={() => setVersionModalVisible(true)}
                >
                  v{versionInfo.version}
                </Tag>
              )}
            </div>
          </div>
        </div>

        {/* 右侧：用户信息 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          {/* 移动端状态指示器 */}
          <div style={{ 
            display: window.innerWidth < 768 ? 'flex' : 'none', 
            gap: '12px',
            marginTop: '4px'
          }}>
            <NetworkStatus />
            <SocketStatus />
          </div>

          {/* 桌面端状态指示器 */}
          <div style={{ 
            display: window.innerWidth >= 768 ? 'flex' : 'none', 
            gap: '16px',
            marginTop: '4px'
          }}>
            <NetworkStatus />
            <SocketStatus />
          </div>

          {/* 移动端余额显示 */}
          <div
            style={{
              display: window.innerWidth < 768 ? 'flex' : 'none',
              alignItems: 'center',
              gap: '1px',
              background: 'var(--background-color)',
              padding: '2px 4px',
              borderRadius: 'var(--border-radius)',
              fontSize: '10px'
            }}
          >
            <WalletOutlined style={{ color: 'var(--success-color)', fontSize: '13px' }} />
            <Text strong style={{ color: 'var(--success-color)', fontSize: '12px' }}>
              ¥{formatCurrency(user?.balance)}
            </Text>
          </div>

          {/* 桌面端余额显示 */}
          <div
            style={{
              display: window.innerWidth >= 768 ? 'flex' : 'none',
              alignItems: 'center',
              gap: '3px',
              background: 'var(--background-color)',
              padding: '3px 8px',
              borderRadius: 'var(--border-radius)'
            }}
          >
            <WalletOutlined style={{ color: 'var(--success-color)', fontSize: '16px' }} />
            <Text strong style={{ color: 'var(--success-color)' }}>
              ¥{formatCurrency(user?.balance)}
            </Text>
          </div>

          {/* 管理员标识 */}
          {user?.role === 'admin' && (
            <Badge 
              count="管理" 
              style={{ 
                display: window.innerWidth < 768 ? 'inline-block' : 'inline-block',
                backgroundColor: 'var(--danger-color)',
                color: 'white',
                padding: '1px 2px',
                borderRadius: '3px',
                fontSize: '7px'
              }} 
            />
          )}

          {/* 用户菜单 */}
          <Dropdown
            menu={{ items: userMenuItems, onClick: handleMenuClick }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button
              type="text"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1px',
                padding: '1px 2px',
                minWidth: 'auto'
              }}
            >
              <Avatar size="small" icon={<UserOutlined />} />
              <span 
                style={{ 
                  color: 'var(--header-text-color)',
                  background: 'var(--header-text-color)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontSize: '12px',
                  display: window.innerWidth >= 768 ? 'block' : 'none'
                }}
              >
                {user?.username}
              </span>
            </Button>
          </Dropdown>
        </div>
      </AntHeader>

      {/* 移动端抽屉菜单 */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {renderLogo('large')}
            <span style={{ color: 'var(--header-text-color)' }}>
              {renderSiteName(false)}
            </span>
          </div>
        }
        open={mobileMenuVisible}
        onClose={() => setMobileMenuVisible(false)}
        placement="left"
        width={280}
        closeIcon={<CloseOutlined />}
        styles={{ 
          body: { padding: 0, background: '#ffffff' },
          header: { background: '#ffffff' }
        }}
      >
        {/* 用户信息区域 */}
        <div style={{ 
          padding: '16px', 
          borderBottom: '1px solid #f0f0f0',
          background: '#ffffff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <Avatar size="large" icon={<UserOutlined />} />
            <div>
              <div style={{ fontWeight: 'bold', color: 'var(--text-color)' }}>
                {user?.username}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-color-secondary)' }}>
                {user?.email}
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <WalletOutlined style={{ color: 'var(--success-color)' }} />
            <Text strong style={{ color: 'var(--success-color)' }}>
              余额: ¥{formatCurrency(user?.balance)}
            </Text>
          </div>
        </div>

        {/* 菜单项 */}
        <div style={{ background: '#ffffff' }}>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            style={{ background: '#ffffff' }}
            items={[
            {
              key: '/dashboard',
              icon: <UserOutlined />,
              label: '仪表板',
              onClick: () => {
                navigate('/dashboard');
                setMobileMenuVisible(false);
              }
            },
            {
              key: '/servers',
              icon: <UserOutlined />,
              label: '服务器管理',
              onClick: () => {
                navigate('/servers');
                setMobileMenuVisible(false);
              }
            },
            {
              key: '/profile',
              icon: <UserOutlined />,
              label: '个人资料',
              onClick: () => {
                navigate('/profile');
                setMobileMenuVisible(false);
              }
            },
            {
              key: '/settings',
              icon: <SettingOutlined />,
              label: '设置',
              onClick: () => {
                navigate('/settings');
                setMobileMenuVisible(false);
              }
            },
            {
              type: 'divider'
            },
            {
              key: 'logout',
              icon: <LogoutOutlined />,
              label: '退出登录',
              onClick: () => {
                logout();
                setMobileMenuVisible(false);
              }
            }
          ]}
          />
        </div>
      </Drawer>

      {/* 版本历史弹窗 */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            <span>版本更新历史</span>
            {versionInfo && (
              <Tag color="blue">当前版本: v{versionInfo.version}</Tag>
            )}
          </Space>
        }
        open={versionModalVisible}
        onCancel={() => setVersionModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setVersionModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {versionInfo ? (
          <div>
            {versionInfo.description && (
              <div style={{ marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
                <Text strong>当前版本描述：</Text>
                <div style={{ marginTop: '8px' }}>{versionInfo.description}</div>
              </div>
            )}
            <Timeline
              items={[
                {
                  color: 'blue',
                  children: (
                    <div>
                      <Text strong>v{versionInfo.version}</Text>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                        当前版本
                        {versionInfo.description && ` - ${versionInfo.description}`}
                      </div>
                    </div>
                  )
                },
                ...(versionInfo.history || []).map((item: any) => ({
                  color: 'gray',
                  children: (
                    <div>
                      <Text strong>v{item.version}</Text>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                        {item.description && `${item.description} - `}
                        {new Date(item.timestamp).toLocaleString('zh-CN')}
                      </div>
                    </div>
                  )
                }))
              ]}
            />
            {(!versionInfo.history || versionInfo.history.length === 0) && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                暂无历史版本记录
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            加载版本信息中...
          </div>
        )}
      </Modal>
    </>
  );
};

export default Header;